import React, { useState, useEffect, useRef } from 'react';
import {
  Dialog,
  DialogContent,
  IconButton,
  Stack,
  Typography,
  useMediaQuery,
  useTheme,
  Box,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import Loading from 'assets/images/loading.png';

import * as pdfjsLib from 'pdfjs-dist';
import 'pdfjs-dist/webpack.mjs';

const BuyerProtectionDialog = ({ open, handleClose, buyerProtectionLink }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const canvasRef = useRef(null);
  const containerRef = useRef(null);

  const [pdfDocument, setPdfDocument] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [pdfUrl, setPdfUrl] = useState(null);

  useEffect(() => {
    if (buyerProtectionLink) {
      const url = buyerProtectionLink.replace('http://', 'https://');
      setPdfUrl(url);
    }
  }, [buyerProtectionLink]);

  useEffect(() => {
    if (!open || !pdfUrl) return;

    const loadPDF = async () => {
      setIsLoading(true);
      setPdfDocument(null);

      try {
        const response = await fetch(pdfUrl);

        if (!response.ok) {
          throw new Error(`Failed to fetch PDF: ${response.status}`);
        }

        const contentType = response.headers.get('content-type');
        console.log('PDF content type:', contentType);

        const arrayBuffer = await response.arrayBuffer();

        console.log('PDF size:', arrayBuffer.byteLength);

        const loadingTask = pdfjsLib.getDocument({
          data: new Uint8Array(arrayBuffer),
          isEvalSupported: false,
        });

        const pdf = await loadingTask.promise;

        setPdfDocument(pdf);
      } catch (err) {
        console.error('PDF loading error:', err);
      } finally {
        setIsLoading(false);
      }
    };

    loadPDF();
  }, [open, pdfUrl]);

  useEffect(() => {
    if (!pdfDocument || !open) return;

    const renderPage = async () => {
      try {
        const page = await pdfDocument.getPage(1);

        const canvas = canvasRef.current;
        if (!canvas) return;

        const context = canvas.getContext('2d');

        const containerWidth = containerRef.current
          ? containerRef.current.clientWidth
          : isMobile
          ? window.innerWidth
          : 700;

        const viewport = page.getViewport({ scale: 1 });

        const cropLeft = 40;
        const cropRight = 40;
        const cropTop = 35;
        const cropBottom = 40;

        const scale = containerWidth / (viewport.width - cropLeft - cropRight);

        const scaledViewport = page.getViewport({ scale });

        canvas.width = scaledViewport.width - (cropLeft + cropRight) * scale;

        canvas.height = scaledViewport.height - (cropTop + cropBottom) * scale;

        const renderContext = {
          canvasContext: context,
          viewport: scaledViewport,
          transform: [1, 0, 0, 1, -cropLeft * scale, -cropTop * scale],
        };

        await page.render(renderContext).promise;
      } catch (err) {
        console.error(err);
      }
    };

    renderPage();
  }, [pdfDocument, open, isMobile]);

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      fullWidth
      maxWidth="md"
      PaperProps={{
        sx: {
          width: isMobile ? '95vw' : '65vw',
          maxWidth: '1200px',
          height: '98vh',
          maxHeight: '98vh',
          borderRadius: isMobile ? '14px' : '20px',
        },
      }}
    >
      <Stack
        direction="row"
        justifyContent="space-between"
        alignItems="center"
        sx={{
          padding: isMobile ? '18px 18px 0px 18px' : '24px 30px 0px 30px',
          flexShrink: 0,
        }}
      >
        <Typography
          sx={{
            fontFamily: 'Poppins',
            fontWeight: 600,
            fontSize: isMobile ? '22px' : '30px',
            color: '#000',
            lineHeight: isMobile ? '34px' : '42px',
          }}
        >
          retreX Buyer Protection
        </Typography>

        <IconButton onClick={handleClose}>
          <CloseIcon
            sx={{
              fontSize: isMobile ? '28px' : '34px',
              color: '#000',
            }}
          />
        </IconButton>
      </Stack>

      <DialogContent
        ref={containerRef}
        sx={{
          p: 0,
          flex: 1,
          display: 'flex',
          overflow: 'auto',
        }}
      >
        {isLoading && (
          <Stack
            alignItems="center"
            justifyContent="center"
            sx={{
              width: '100%',
              height: '100%',
              minHeight: '100%',
              flex: 1,
            }}
          >
            <img
              alt="Loading"
              src={Loading}
              className="loaderImage"
              style={{
                height: '40px',
                width: '40px',
                animation: 'spin 1s linear infinite',
              }}
            />
          </Stack>
        )}

        {!isLoading && pdfDocument && (
          <Stack
            sx={{
              width: '100%',
              alignItems: 'center',
              py: 0,
            }}
          >
            <canvas
              ref={canvasRef}
              style={{
                width: '100%',
                height: 'auto',
                display: 'block',
              }}
            />
          </Stack>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default BuyerProtectionDialog;
